/**
 * PortalFileCard - 门户文件卡片组件
 * 
 * macOS Finder 风格设计：
 * - 卡片只包含预览区域
 * - 文件名和元信息在卡片下方
 * - 大圆角、柔和阴影
 * - 优雅的渐变占位符
 */

import React, { useState, useCallback, useMemo } from 'react';
import { 
  Eye, Download, FileText, Video, Music, Image, File, 
  Play, Check, FileSpreadsheet, Folder
} from 'lucide-react';
import { KnowledgeFile } from '../../types';
import { formatFileSize } from '../../utils/file-types';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n';

interface PortalFileCardProps {
  file: KnowledgeFile;
  thumbnailUrl?: string;
  onPreview?: (file: KnowledgeFile) => void;
  onDownload?: (file: KnowledgeFile) => void;
  canPreview?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (fileId: string) => void;
  // 🆕 搜索结果显示来源文件夹
  sourceFolder?: { id: string; name: string } | null;
  onSourceFolderClick?: (folderId: string) => void;
}

const PortalFileCard: React.FC<PortalFileCardProps> = ({
  file,
  thumbnailUrl,
  onPreview,
  onDownload,
  canPreview = true,
  selectable = false,
  selected = false,
  onToggleSelect,
  sourceFolder,
  onSourceFolderClick
}) => {
  const { t } = useI18n();
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // 文件类型配置 - 优雅的渐变色
  const fileTypeConfig = useMemo(() => {
    const configs: Record<string, { 
      icon: React.ReactNode; 
      gradient: string;
      label: string;
    }> = {
      pdf: { 
        icon: <FileText className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-rose-400 via-red-400 to-pink-500',
        label: 'PDF'
      },
      video: { 
        icon: <Video className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-violet-400 via-purple-500 to-indigo-500',
        label: 'VIDEO'
      },
      audio: { 
        icon: <Music className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-pink-400 via-rose-400 to-red-400',
        label: 'AUDIO'
      },
      image: { 
        icon: <Image className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-emerald-400 via-teal-400 to-cyan-500',
        label: 'IMAGE'
      },
      document: { 
        icon: <FileText className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-blue-400 via-sky-400 to-cyan-400',
        label: 'DOC'
      },
      spreadsheet: { 
        icon: <FileSpreadsheet className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-green-400 via-emerald-400 to-teal-400',
        label: 'XLS'
      },
      folder: { 
        icon: <Folder className="w-10 h-10 text-white drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-amber-400 via-orange-400 to-yellow-400',
        label: 'FOLDER'
      },
      other: { 
        icon: <File className="w-10 h-10 text-white/80 drop-shadow-md" strokeWidth={1.5} />,
        gradient: 'from-slate-300 via-gray-300 to-zinc-400 dark:from-slate-600 dark:via-gray-600 dark:to-zinc-700',
        label: 'FILE'
      }
    };
    
    const type = file.type?.toLowerCase() || 'other';
    const ext = file.name?.split('.').pop()?.toLowerCase();
    if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
      return configs.spreadsheet;
    }
    return configs[type] || configs.other;
  }, [file.type, file.name]);

  // 权限信息
  const permissionInfo = useMemo(() => {
    if (file.is_public) {
      return { text: t('permissions.public'), dotColor: 'bg-emerald-500' };
    }
    const level = file.min_permission_level || 'basic';
    const colors: Record<string, string> = {
      admin: 'bg-rose-500',
      advanced: 'bg-amber-500',
      intermediate: 'bg-sky-500',
      basic: 'bg-blue-500'
    };
    return { 
      text: t(`permissions.${level}`), 
      dotColor: colors[level] || colors.basic 
    };
  }, [file.is_public, file.min_permission_level, t]);

  const handlePreviewClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (canPreview && onPreview) {
      onPreview(file);
    }
  }, [canPreview, onPreview, file]);

  const handleDownloadClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(file);
    }
  }, [onDownload, file]);

  const handleSelectClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(file.id);
    }
  }, [onToggleSelect, file.id]);

  const handleCardClick = useCallback(() => {
    if (selectable && onToggleSelect) {
      onToggleSelect(file.id);
    } else if (canPreview && onPreview) {
      onPreview(file);
    }
  }, [selectable, onToggleSelect, canPreview, onPreview, file]);

  const actualThumbnailUrl = thumbnailUrl || (file.thumbnail_key 
    ? `https://file.unilumin-gtm.com/${file.thumbnail_key}` 
    : null);

  const hasThumbnail = actualThumbnailUrl && !imageError;

  return (
    <div className="portal-file-card-wrapper group" onClick={handleCardClick}>
      {/* 预览卡片 - 16:9 宽屏比例 */}
      <div
        className={cn(
          "relative aspect-video overflow-hidden cursor-pointer",
          // macOS 风格大圆角
          "rounded-[20px]",
          // 柔和阴影
          "shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
          "dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)]",
          // 悬停效果
          "transition-all duration-300 ease-out",
          "hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)]",
          "dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.5)]",
          "hover:-translate-y-1",
          // 选中状态
          selected && "ring-[3px] ring-blue-500 ring-offset-2 dark:ring-offset-gray-900"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {hasThumbnail ? (
          <>
            <img
              src={actualThumbnailUrl}
              alt={file.name}
              className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
              onError={() => setImageError(true)}
              loading="lazy"
            />
            {/* 视频播放按钮 */}
            {file.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border-2 border-white/30 transition-transform duration-300 group-hover:scale-110">
                  <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                </div>
              </div>
            )}
          </>
        ) : (
          /* 优雅的渐变占位符 */
          <div className={cn(
            "w-full h-full flex flex-col items-center justify-center bg-gradient-to-br",
            fileTypeConfig.gradient
          )}>
            {fileTypeConfig.icon}
            <span className="mt-2 text-[11px] font-bold tracking-[0.15em] text-white/90 uppercase">
              {fileTypeConfig.label}
            </span>
          </div>
        )}

        {/* 批量选择复选框 */}
        {selectable && (
          <button
            onClick={handleSelectClick}
            className={cn(
              "absolute top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200",
              selected 
                ? "bg-blue-500 text-white shadow-lg" 
                : "bg-white/90 dark:bg-black/50 backdrop-blur-sm border border-gray-200/50 dark:border-white/20 text-gray-400 hover:text-gray-600 dark:text-gray-400"
            )}
          >
            {selected ? (
              <Check className="w-4 h-4" strokeWidth={3} />
            ) : (
              <div className="w-4 h-4 border-2 border-current rounded-full" />
            )}
          </button>
        )}

        {/* 悬停操作层 */}
        <div className={cn(
          "absolute inset-0 flex items-center justify-center gap-4",
          "bg-black/30 backdrop-blur-[2px]",
          "transition-all duration-200",
          isHovered && !selectable ? "opacity-100" : "opacity-0 pointer-events-none"
        )}>
          <button
            onClick={handlePreviewClick}
            disabled={!canPreview}
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200",
              canPreview 
                ? "bg-white/95 text-gray-700 hover:scale-110 shadow-xl"
                : "bg-white/50 text-gray-400 cursor-not-allowed"
            )}
            title={t('portal.preview')}
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={handleDownloadClick}
            className="w-12 h-12 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center shadow-xl transition-all duration-200 hover:scale-110"
            title={t('portal.download')}
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* 文件信息 - 在卡片下方 */}
      <div className="mt-3 px-1">
        {/* 文件名 */}
        <h3 
          className="font-semibold text-gray-900 dark:text-white text-[14px] leading-snug truncate"
          title={file.name}
        >
          {file.name}
        </h3>
        
        {/* 🆕 来源文件夹 - 搜索结果时显示 */}
        {sourceFolder && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSourceFolderClick?.(sourceFolder.id);
            }}
            className="flex items-center gap-1 mt-1 text-[11px] text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors group/folder"
          >
            <Folder className="w-3 h-3 opacity-70 group-hover/folder:opacity-100" />
            <span className="truncate max-w-[120px]">{sourceFolder.name}</span>
          </button>
        )}

        {/* 元信息行：类型 + 大小 */}
        <div className={cn("flex items-center justify-between", sourceFolder ? "mt-1" : "mt-1")}>
          <span className="text-[12px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            {file.type?.toUpperCase() || 'FILE'}
          </span>
          <span className="text-[12px] text-gray-400 dark:text-gray-500">
            {formatFileSize(file.size)}
          </span>
        </div>

        {/* 权限 + 标签行 */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {/* 权限指示 */}
          <div className="flex items-center gap-1.5">
            <span className={cn("w-[6px] h-[6px] rounded-full", permissionInfo.dotColor)} />
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              {permissionInfo.text}
            </span>
          </div>

          {/* 标签 */}
          {file.tags && file.tags.length > 0 && (
            <>
              <span className="w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-gray-600" />
              {file.tags.slice(0, 2).map((tag) => (
                <span 
                  key={tag.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium"
                  style={{ 
                    backgroundColor: `${tag.color}15`, 
                    color: tag.color
                  }}
                >
                  {tag.name}
                </span>
              ))}
              {file.tags.length > 2 && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  +{file.tags.length - 2}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalFileCard;
